import { SkillCategory } from '../../types/skillProgress';

export const padelSkillCategories: SkillCategory[] = [
  {
    id: 'serving',
    name: 'Serving',
    description: 'Master the art of serving in padel',
    skills: [
      {
        id: 'basic-serve',
        name: 'Basic Serve',
        description: 'Execute a legal underhand serve that clears the net',
        difficulty: 'beginner',
        tips: [
          'Use an underhand motion',
          'Contact the ball below your waist',
          'Follow through toward your target',
          'Keep your feet behind the service line'
        ]
      },
      {
        id: 'serve-placement',
        name: 'Serve Placement',
        description: 'Place serves to different areas of the court',
        difficulty: 'beginner',
        prerequisites: ['basic-serve'],
        tips: [
          'Aim for the corners to create angles',
          'Use the glass walls to your advantage',
          'Mix up deep and short serves',
          'Target your opponent\'s weaker side'
        ]
      },
      {
        id: 'power-serve',
        name: 'Power Serve',
        description: 'Add pace and spin to your serve',
        difficulty: 'intermediate',
        prerequisites: ['basic-serve', 'serve-placement'],
        tips: [
          'Generate power from your legs and core',
          'Use spin to control the bounce',
          'Practice consistency over power',
          'Use the glass for strategic bounces'
        ]
      },
      {
        id: 'spin-serve',
        name: 'Spin Serve',
        description: 'Master different types of spin serves',
        difficulty: 'advanced',
        prerequisites: ['power-serve'],
        tips: [
          'Practice topspin and backspin',
          'Use spin to control bounce height',
          'Vary spin to keep opponents guessing',
          'Focus on consistency and placement'
        ]
      }
    ]
  },
  {
    id: 'groundstrokes',
    name: 'Groundstrokes',
    description: 'Develop consistent groundstroke technique',
    skills: [
      {
        id: 'forehand-basic',
        name: 'Basic Forehand',
        description: 'Execute a proper forehand groundstroke',
        difficulty: 'beginner',
        tips: [
          'Use a continental grip',
          'Keep your paddle face square to the ball',
          'Follow through across your body',
          'Stay balanced throughout the stroke'
        ]
      },
      {
        id: 'backhand-basic',
        name: 'Basic Backhand',
        description: 'Execute a proper backhand groundstroke',
        difficulty: 'beginner',
        tips: [
          'Use a continental or eastern backhand grip',
          'Keep your non-dominant hand on the paddle',
          'Rotate your shoulders for power',
          'Follow through toward your target'
        ]
      },
      {
        id: 'wall-play',
        name: 'Wall Play',
        description: 'Use the glass walls effectively',
        difficulty: 'intermediate',
        prerequisites: ['forehand-basic', 'backhand-basic'],
        tips: [
          'Understand ball angles off the glass',
          'Position yourself for wall rebounds',
          'Use walls to create difficult shots',
          'Practice reading ball trajectories'
        ]
      },
      {
        id: 'power-groundstroke',
        name: 'Power Groundstroke',
        description: 'Add pace and power to groundstrokes',
        difficulty: 'intermediate',
        prerequisites: ['wall-play'],
        tips: [
          'Generate power from your legs and core',
          'Use proper weight transfer',
          'Keep your head still during contact',
          'Follow through completely'
        ]
      }
    ]
  },
  {
    id: 'net-play',
    name: 'Net Play',
    description: 'Master volleys and overheads at the net',
    skills: [
      {
        id: 'volley-basic',
        name: 'Basic Volley',
        description: 'Execute a proper volley at the net',
        difficulty: 'intermediate',
        tips: [
          'Keep your paddle up and ready',
          'Use a short, compact swing',
          'Step into the ball',
          'Keep your eyes on the ball'
        ]
      },
      {
        id: 'volley-placement',
        name: 'Volley Placement',
        description: 'Place volleys to different areas of the court',
        difficulty: 'intermediate',
        prerequisites: ['volley-basic'],
        tips: [
          'Aim for the corners of the court',
          'Use angles to create openings',
          'Mix up placement to keep opponents guessing',
          'Focus on consistency over power'
        ]
      },
      {
        id: 'overhead-smash',
        name: 'Overhead Smash',
        description: 'Execute a powerful overhead shot',
        difficulty: 'advanced',
        prerequisites: ['volley-basic'],
        tips: [
          'Get into position quickly',
          'Use a continental grip',
          'Hit the ball at the highest point',
          'Follow through toward your target'
        ]
      },
      {
        id: 'bandeja',
        name: 'Bandeja',
        description: 'Master the signature padel shot',
        difficulty: 'advanced',
        prerequisites: ['volley-placement'],
        tips: [
          'Use a continental grip',
          'Hit with topspin and slice',
          'Aim for the opponent\'s feet',
          'Follow through across your body'
        ]
      }
    ]
  },
  {
    id: 'strategy',
    name: 'Strategy & Tactics',
    description: 'Develop strategic thinking and court awareness',
    skills: [
      {
        id: 'court-positioning',
        name: 'Court Positioning',
        description: 'Understand proper court positioning in padel',
        difficulty: 'beginner',
        tips: [
          'Stay in the center of your side',
          'Move as a team in doubles',
          'Cover your partner\'s weaknesses',
          'Use the glass walls strategically'
        ]
      },
      {
        id: 'shot-selection',
        name: 'Shot Selection',
        description: 'Choose the right shot for each situation',
        difficulty: 'intermediate',
        prerequisites: ['court-positioning'],
        tips: [
          'Consider your opponent\'s position',
          'Use the glass walls to your advantage',
          'Don\'t try to win every point with power',
          'Be patient and wait for opportunities'
        ]
      },
      {
        id: 'doubles-strategy',
        name: 'Doubles Strategy',
        description: 'Develop effective doubles tactics',
        difficulty: 'intermediate',
        prerequisites: ['shot-selection'],
        tips: [
          'Communicate with your partner',
          'Cover the court as a team',
          'Use the glass walls strategically',
          'Exploit your opponent\'s weaknesses'
        ]
      },
      {
        id: 'advanced-strategy',
        name: 'Advanced Strategy',
        description: 'Develop sophisticated strategic thinking',
        difficulty: 'advanced',
        prerequisites: ['doubles-strategy'],
        tips: [
          'Study your opponent\'s patterns',
          'Use deception and variety',
          'Control the pace of the game',
          'Master the glass wall angles'
        ]
      }
    ]
  },
  {
    id: 'mental-game',
    name: 'Mental Game',
    description: 'Develop mental toughness and focus',
    skills: [
      {
        id: 'focus-concentration',
        name: 'Focus & Concentration',
        description: 'Maintain focus throughout the match',
        difficulty: 'beginner',
        tips: [
          'Stay in the present moment',
          'Don\'t dwell on mistakes',
          'Focus on the next point',
          'Use breathing techniques to stay calm'
        ]
      },
      {
        id: 'pressure-handling',
        name: 'Pressure Handling',
        description: 'Perform well under pressure',
        difficulty: 'intermediate',
        prerequisites: ['focus-concentration'],
        tips: [
          'Practice pressure situations',
          'Develop pre-shot routines',
          'Stay positive and confident',
          'Use visualization techniques'
        ]
      },
      {
        id: 'match-strategy',
        name: 'Match Strategy',
        description: 'Develop and execute match strategies',
        difficulty: 'advanced',
        prerequisites: ['pressure-handling'],
        tips: [
          'Adapt your strategy during the match',
          'Exploit your opponent\'s weaknesses',
          'Stay patient and disciplined',
          'Make adjustments as needed'
        ]
      }
    ]
  }
];
